import { PincodeService } from '@billx/api';
import type { OutletInfoData } from '@billx/forms';
import { COUNTRIES, getCountryCodeByName } from '@billx/utils';
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  keyframes,
  MenuItem,
  Select,
  Stack,
  TextField,
  type Theme,
  useTheme,
} from '@mui/material';
import { type ChangeEvent, useCallback, useState } from 'react';
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useController,
  useFormContext,
} from 'react-hook-form';

import { FieldLabel } from '../FieldLabel';
import { FloatingHelperTextField } from '../FloatingHelperTextField';

const getBorderHighlightAnimation = (theme: Theme) => keyframes`
  50% {
    border-color: ${theme.palette.success.main};
    border-width: 2px;
  }
`;

interface OutletInfoFormProps<TFieldValues extends FieldValues> {
  disabled?: boolean;
  fieldPrefix?: FieldPath<TFieldValues>;
}

const PINCODE_LENGTH = 6;

export function OutletInfoForm<TFieldValues extends FieldValues>({
  disabled = false,
  fieldPrefix,
}: OutletInfoFormProps<TFieldValues>) {
  const theme = useTheme();
  const [isFetchingPincode, setIsFetchingPincode] = useState(false);
  const [autoFilledFields, setAutoFilledFields] = useState<Set<string>>(
    new Set()
  );

  const fieldName = useCallback(
    (name: keyof OutletInfoData) =>
      (fieldPrefix
        ? `${fieldPrefix}.${name}`
        : name) as FieldPath<TFieldValues>,
    [fieldPrefix]
  );

  const { control, setValue } = useFormContext<TFieldValues>();
  const { field: pincodeField, fieldState: pincodeFieldState } = useController({
    name: fieldName('zipcode'),
    control,
  });

  const handleZipcodeChange = async (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    pincodeField.onChange(e);

    if (!pincodeFieldState.isDirty || value.length < PINCODE_LENGTH) {
      return;
    }

    if (isFetchingPincode) {
      return;
    }

    setIsFetchingPincode(true);

    try {
      const data = await PincodeService.fetchPincodeData(value);

      if (data) {
        const filledFields = new Set<string>();

        if (data.State) {
          setValue(fieldName('state'), data.State as never, {
            shouldValidate: true,
          });
          filledFields.add('state');
        }

        if (data.Country) {
          const country = getCountryCodeByName(data.Country);
          if (country) {
            setValue(fieldName('country'), country as never, {
              shouldValidate: true,
            });
            filledFields.add('country');
          }
        }

        if (data.District) {
          setValue(fieldName('city'), data.District as never, {
            shouldValidate: true,
          });
          filledFields.add('city');
        }

        if (filledFields.size > 0) {
          setAutoFilledFields(filledFields);
          setTimeout(() => {
            setAutoFilledFields(new Set());
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error fetching pincode data:', error);
    } finally {
      setIsFetchingPincode(false);
    }
  };

  const gap = { xs: 2, sm: 3 };

  return (
    <Stack gap={gap}>
      <Controller
        name={fieldName('name')}
        control={control}
        render={({ field, fieldState }) => (
          <Box>
            <FieldLabel
              htmlFor={String(fieldName('name'))}
              label="Outlet Name"
              required
              subtitle="Add a name to identify your business outlet"
            />
            <FloatingHelperTextField
              {...field}
              addMarginBottom={false}
              fullWidth
              placeholder="Outlet Name"
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              disabled={disabled}
              required
            />
          </Box>
        )}
      />

      <Controller
        name={fieldName('street')}
        control={control}
        render={({ field, fieldState }) => (
          <Box>
            <FieldLabel
              htmlFor={String(fieldName('street'))}
              label="Street Address"
              required
              subtitle="Enter the street address of your business outlet"
            />
            <FloatingHelperTextField
              {...field}
              addMarginBottom={false}
              fullWidth
              placeholder="Street Address"
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
              disabled={disabled}
              required
            />
          </Box>
        )}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap,
        }}
      >
        <Box>
          <FieldLabel htmlFor="zipcode" label="Zipcode" required />
          <FloatingHelperTextField
            {...pincodeField}
            onChange={handleZipcodeChange}
            addMarginBottom={false}
            fullWidth
            placeholder="Zipcode"
            inputProps={{
              minLength: PINCODE_LENGTH,
              maxLength: PINCODE_LENGTH,
            }}
            InputProps={{
              endAdornment: isFetchingPincode ? (
                <CircularProgress size={20} />
              ) : null,
            }}
            error={pincodeFieldState.invalid}
            helperText={pincodeFieldState.error?.message}
            disabled={disabled}
            required
          />
        </Box>
        <Controller
          name={fieldName('city')}
          control={control}
          render={({ field, fieldState }) => (
            <Box>
              <FieldLabel htmlFor="city" label="City" required />
              <FloatingHelperTextField
                {...field}
                addMarginBottom={false}
                fullWidth
                placeholder="City"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                disabled={disabled}
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    animation: autoFilledFields.has('city')
                      ? `${getBorderHighlightAnimation(theme)} 1s ease-in-out`
                      : 'none',
                  },
                }}
              />
            </Box>
          )}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap,
        }}
      >
        <Controller
          name={fieldName('state')}
          control={control}
          render={({ field, fieldState }) => (
            <Box>
              <FieldLabel htmlFor="state" label="State/Provice" required />
              <TextField
                {...field}
                fullWidth
                placeholder="State/Provice"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                disabled={disabled}
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    animation: autoFilledFields.has('state')
                      ? `${getBorderHighlightAnimation(theme)} 1s ease-in-out`
                      : 'none',
                  },
                }}
              />
            </Box>
          )}
        />
        <Controller
          name={fieldName('country')}
          control={control}
          render={({ field, fieldState }) => (
            <Box>
              <FieldLabel htmlFor="country" label="Country" required />
              <FormControl
                fullWidth
                error={fieldState.invalid}
                disabled={disabled}
                required
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    animation: autoFilledFields.has('country')
                      ? `${getBorderHighlightAnimation(theme)} 1s ease-in-out`
                      : 'none',
                  },
                }}
              >
                <Select
                  {...field}
                  value={field.value || ''}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  {COUNTRIES.map((country: any) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.flag} {country.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText
                  error={fieldState.invalid}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    transform: 'translateY(120%)',
                  }}
                >
                  {fieldState.error?.message}
                </FormHelperText>
              </FormControl>
            </Box>
          )}
        />
      </Box>
    </Stack>
  );
}
